import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, EmptyState } from "@/components/Shell";
import StatusBadge from "@/components/StatusBadge";
import FileUpload from "@/components/FileUpload";
import { Trash2, Plus, FileText, ChevronLeft, Send, Paperclip } from "lucide-react";

export function RequestList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");
  const load = () => api.get("/requests").then(r => setItems(r.data)).catch(e => setErr(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const filtered = filter ? items.filter(i => i.status === filter) : items;

  return (
    <div className="p-4 md:p-8" data-testid="requests-page">
      <PageHeader
        title="Material Requests"
        subtitle="Approve, issue, and track all engineer material requests."
        action={
          <Link to="/requests/new" data-testid="new-request-btn" className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md">
            <Plus size={14} /> New Request
          </Link>
        }
      />
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {["", "Pending CH Approval", "Pending Admin Approval", "Ready for Issue", "Pending Engineer Usage Update", "Used", "Rejected"].map(s => (
          <button key={s} onClick={()=>setFilter(s)} data-testid={`filter-${s||"all"}`}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold border whitespace-nowrap ${filter===s ? "bg-[#F97316] text-white border-[#F97316]" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>
            {s || "All"}
          </button>
        ))}
      </div>

      {err && <div className="text-rose-600 text-sm mb-3">{err}</div>}
      {filtered.length === 0 ? <EmptyState title="No requests found" icon={FileText} /> :
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm data-table">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-4 py-3 text-left">Request</th>
                  <th className="px-4 py-3 text-left">Requester</th>
                  <th className="px-4 py-3 text-left">Customer</th>
                  <th className="px-4 py-3 text-left">Project/Circuit</th>
                  <th className="px-4 py-3 text-left">Priority</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <Link to={`/requests/${r.id}`} className="font-semibold text-[#F97316] hover:underline" data-testid={`req-link-${r.request_no}`}>{r.request_no}</Link>
                      <div className="text-[11px] text-zinc-500">{r.request_type}</div>
                    </td>
                    <td className="px-4 py-3">{r.requester_name}<div className="text-[11px] text-zinc-500 capitalize">{r.requester_role.replace("_", " ")}</div></td>
                    <td className="px-4 py-3">{r.customer_name || "—"}</td>
                    <td className="px-4 py-3 num">{r.project_id || "—"}</td>
                    <td className="px-4 py-3">{r.priority}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={r.status} />
                      {r.last_approval && <div className="text-[10px] text-zinc-500 mt-1">{r.last_approval.action} by {r.last_approval.by_name}</div>}
                    </td>
                    <td className="px-4 py-3 text-zinc-500 num">{(r.created_at||"").slice(0,16).replace("T"," ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      }
    </div>
  );
}

export function RequestNew() {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    customer_name: "", customer_address: "", project_id: "", request_type: "Maintenance",
    priority: "Normal", required_date: "", remark: "", items: [{ product_id: "", quantity: 1, remark: "" }],
    attachments: [],
  });
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/products").then(r => setProducts(r.data));
    api.get("/projects").then(r => setProjects(r.data));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setForm(f => {
    const items = [...f.items]; items[i] = { ...items[i], [k]: v }; return { ...f, items };
  });
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { product_id: "", quantity: 1, remark: "" }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const payload = {
        ...form,
        items: form.items.filter(it => it.product_id && it.quantity > 0).map(it => ({...it, quantity: parseInt(it.quantity)})),
        attachments: attachedFiles.map(f => f.id),
      };
      if (payload.items.length === 0) { setErr("Add at least one item"); setBusy(false); return; }
      const { data } = await api.post("/requests", payload);
      nav(`/requests/${data.id}`);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto" data-testid="new-request-page">
      <button onClick={()=>nav(-1)} className="text-xs text-zinc-500 hover:text-[#F97316] mb-3 inline-flex items-center gap-1"><ChevronLeft size={14}/> Back</button>
      <PageHeader title="Raise Material Request" subtitle="Field engineers can submit requests; cluster head approves." />

      <form onSubmit={submit} className="space-y-5" data-testid="new-request-form">
        <Card className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Customer Name">
              <input data-testid="req-customer-input" value={form.customer_name} onChange={e=>set("customer_name", e.target.value)} className="input" />
            </Field>
            <Field label="Project / Circuit ID" hint="Case-insensitive. Auto-created if new.">
              <input data-testid="req-project-input" list="projects" value={form.project_id} onChange={e=>set("project_id", e.target.value)} className="input" placeholder="SIPL-ILL-1001" />
              <datalist id="projects">{projects.map(p => <option key={p.id} value={p.project_id} />)}</datalist>
            </Field>
            <Field label="Customer Address">
              <input data-testid="req-address-input" value={form.customer_address} onChange={e=>set("customer_address", e.target.value)} className="input" />
            </Field>
            <Field label="Request Type">
              <select data-testid="req-type-select" value={form.request_type} onChange={e=>set("request_type", e.target.value)} className="input">
                {["New Project","New Link","Maintenance","Replacement","Upgrade","Shifting","Internal Work"].map(x => <option key={x}>{x}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select data-testid="req-priority-select" value={form.priority} onChange={e=>set("priority", e.target.value)} className="input">
                {["Normal","Urgent","Critical"].map(x => <option key={x}>{x}</option>)}
              </select>
            </Field>
            <Field label="Required Date">
              <input data-testid="req-date-input" type="date" value={form.required_date} onChange={e=>set("required_date", e.target.value)} className="input" />
            </Field>
            <Field label="Remark" full>
              <textarea data-testid="req-remark-input" value={form.remark} onChange={e=>set("remark", e.target.value)} rows={2} className="input" />
            </Field>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-semibold">Items</div>
            <button type="button" onClick={addItem} data-testid="add-item-btn" className="text-xs font-semibold inline-flex items-center gap-1 text-[#F97316] hover:underline">
              <Plus size={13} /> Add item
            </button>
          </div>
          <div className="space-y-3">
            {form.items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-start border-b border-zinc-100 pb-3">
                <div className="col-span-12 md:col-span-6">
                  <select data-testid={`item-product-${i}`} value={it.product_id} onChange={e=>setItem(i, "product_id", e.target.value)} className="input">
                    <option value="">Select product</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
                  </select>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <input data-testid={`item-qty-${i}`} type="number" min="1" value={it.quantity} onChange={e=>setItem(i, "quantity", e.target.value)} className="input" placeholder="Qty" />
                </div>
                <div className="col-span-5 md:col-span-3">
                  <input data-testid={`item-remark-${i}`} value={it.remark} onChange={e=>setItem(i, "remark", e.target.value)} className="input" placeholder="Remark" />
                </div>
                <button type="button" onClick={()=>removeItem(i)} className="col-span-1 text-zinc-400 hover:text-rose-600 py-2"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-zinc-100">
            <div className="text-[11px] uppercase tracking-widest font-bold text-zinc-700 mb-2">Attachments</div>
            <FileUpload value={attachedFiles} onChange={setAttachedFiles} testId="req-attach" />
          </div>
        </Card>

        {err && <div data-testid="form-error" className="text-sm font-medium text-rose-600">{err}</div>}
        <div className="sticky bottom-0 md:static bg-white md:bg-transparent py-3 md:py-0">
          <button data-testid="submit-request-btn" type="submit" disabled={busy} className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2.5 px-6 rounded-md disabled:opacity-60">
            <Send size={14} /> Submit Request
          </button>
        </div>
      </form>
      <style>{`.input { width:100%; border:1px solid #E4E4E7; background:#fff; border-radius:.375rem; padding:.5rem .75rem; font-size:.85rem; outline:none } .input:focus { box-shadow: 0 0 0 2px #F97316; border-color: transparent }`}</style>
    </div>
  );
}

function Field({ label, hint, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-700 mb-1.5">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

export function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  const [warehouses, setWarehouses] = useState([]);
  const [issueWh, setIssueWh] = useState("");
  const [actionRemark, setActionRemark] = useState("");
  const [issueItems, setIssueItems] = useState([]);
  const [skipSerial, setSkipSerial] = useState(false);
  const [availableSerials, setAvailableSerials] = useState({}); // {product_id: [serial_no...]}
  const [usage, setUsage] = useState({ project_id: "", customer_name: "", site_address: "", usage_location: "CE Side", pop_id: "", installation_date: "", remark: "", items: [], attachments: [] });
  const [usageAttachments, setUsageAttachments] = useState([]);
  const [doRecovery, setDoRecovery] = useState(false);
  const [recoveryForm, setRecoveryForm] = useState({ recovered_from: "CE Side", returned_to_warehouse_id: "", remark: "",
    items: [{ product_id: "", quantity: 1, condition: "Working", serial_numbers: "" }] });

  const load = () => api.get(`/requests/${id}`).then(rr => {
    setR(rr.data);
    setIssueItems(rr.data.items.map(it => ({ product_id: it.product_id, quantity: it.quantity, serial_numbers: [], remark: "" })));
    setUsage(u => ({ ...u, project_id: rr.data.project_id || "", customer_name: rr.data.customer_name || "", site_address: rr.data.customer_address || "", items: rr.data.items.map(it => ({ product_id: it.product_id, quantity: it.quantity, serial_numbers: [], remark: "" })) }));
  }).catch(e => setErr(formatApiError(e)));
  useEffect(() => { load(); api.get("/warehouses").then(r => setWarehouses(r.data)); }, [id]);

  if (err) return <div className="p-6 text-rose-600 text-sm">{err}</div>;
  if (!r) return <div className="p-6 text-sm text-zinc-500">Loading…</div>;

  const action = async (kind) => {
    try {
      await api.post(`/requests/${id}/approve`, { action: kind, remark: actionRemark });
      setActionRemark(""); load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const issue = async () => {
    try {
      const items = issueItems.map(it => ({...it, quantity: parseInt(it.quantity) || 0,
        serial_numbers: Array.isArray(it.serial_numbers) ? it.serial_numbers :
          (it.serial_numbers || "").split(",").map(s=>s.trim()).filter(Boolean),
      }));
      await api.post(`/requests/${id}/issue`, { warehouse_id: issueWh, handover_mode: "Direct handover", remark: actionRemark, items, skip_serial_capture: skipSerial });
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const submitUsage = async () => {
    try {
      const items = usage.items.map(it => ({...it, quantity: parseInt(it.quantity) || 0,
        serial_numbers: typeof it.serial_numbers === "string" ? it.serial_numbers.split(",").map(s=>s.trim()).filter(Boolean) : it.serial_numbers,
      }));
      await api.post(`/requests/${id}/usage`, { ...usage, items, attachments: usageAttachments.map(f => f.id) });

      // If engineer also chose to submit a recovery in-line
      if (doRecovery && recoveryForm.items.some(it => it.product_id && it.quantity > 0)) {
        const recItems = recoveryForm.items.filter(it => it.product_id && it.quantity > 0).map(it => ({
          product_id: it.product_id, quantity: parseInt(it.quantity) || 0, condition: it.condition,
          serial_numbers: (it.serial_numbers || "").split(",").map(s=>s.trim()).filter(Boolean),
        }));
        await api.post(`/recoveries`, {
          project_id: usage.project_id, customer_name: usage.customer_name,
          site_address: usage.site_address, recovered_from: recoveryForm.recovered_from,
          recovery_date: new Date().toISOString().slice(0,10),
          returned_to_warehouse_id: recoveryForm.returned_to_warehouse_id || null,
          remark: recoveryForm.remark, items: recItems, attachments: [],
        });
      }
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const canApprove = (user.role === "cluster_head" && r.status === "Pending CH Approval") ||
                     (user.role === "admin" && ["Pending CH Approval", "Pending Admin Approval", "Submitted"].includes(r.status));
  const canIssue = ["admin","warehouse_manager"].includes(user.role) && ["Ready for Issue", "Partially Approved", "Approved"].includes(r.status);
  const canUpdateUsage = (user.id === r.requester_id || user.role === "admin") && ["Pending Engineer Usage Update", "Issued"].includes(r.status);

  return (
    <div className="p-4 md:p-8" data-testid="request-detail-page">
      <button onClick={()=>nav(-1)} className="text-xs text-zinc-500 hover:text-[#F97316] mb-3 inline-flex items-center gap-1"><ChevronLeft size={14}/> Back</button>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#F97316]">{r.request_type}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">{r.request_no}</h1>
          <div className="text-sm text-zinc-500 mt-1">{r.customer_name} · {r.project_id || "No project yet"}</div>
        </div>
        <StatusBadge status={r.status} className="text-sm px-3 py-1" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-semibold">Items</div>
              {r.total_cost !== undefined && (
                <div className="text-xs"><span className="text-zinc-500 mr-2">Total cost</span><span className="font-display font-bold text-base num text-[#F97316]">₹{r.total_cost.toLocaleString("en-IN")}</span></div>
              )}
            </div>
            <table className="w-full text-sm data-table">
              <thead className="bg-zinc-50"><tr><th className="px-3 py-2 text-left">SKU</th><th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-right">Qty</th>{r.total_cost !== undefined && <th className="px-3 py-2 text-right">Line Total</th>}<th className="px-3 py-2 text-left">Serial</th></tr></thead>
              <tbody>
                {r.items.map((it, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    <td className="px-3 py-2 num">{it.sku}</td>
                    <td className="px-3 py-2">{it.product_name}</td>
                    <td className="px-3 py-2 num text-right font-bold">{it.quantity}</td>
                    {r.total_cost !== undefined && <td className="px-3 py-2 num text-right">₹{(it.line_total || 0).toLocaleString("en-IN")}</td>}
                    <td className="px-3 py-2 text-zinc-500 text-xs">{(it.serial_numbers || []).join(", ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {canApprove && (
            <Card className="p-5 space-y-3" data-testid="approval-card">
              <div className="font-display font-semibold">Approval</div>
              <textarea placeholder="Add remark (optional)" value={actionRemark} onChange={e=>setActionRemark(e.target.value)} rows={2} className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm" data-testid="approval-remark" />
              <div className="flex gap-2 flex-wrap">
                <button onClick={()=>action("approve")} data-testid="approve-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm py-2 px-4 rounded-md">Approve</button>
                <button onClick={()=>action("partial")} data-testid="partial-btn" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm py-2 px-4 rounded-md">Partial</button>
                <button onClick={()=>action("reject")} data-testid="reject-btn" className="bg-rose-600 hover:bg-rose-700 text-white font-semibold text-sm py-2 px-4 rounded-md">Reject</button>
              </div>
            </Card>
          )}

          {canIssue && (
            <Card className="p-5 space-y-3" data-testid="issue-card">
              <div className="font-display font-semibold">Issue Material</div>
              <select value={issueWh} onChange={e=>setIssueWh(e.target.value)} className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm" data-testid="issue-warehouse">
                <option value="">Choose warehouse</option>
                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
              <div className="space-y-2">
                {issueItems.map((it, i) => {
                  const reqItem = r.items[i];
                  const isSer = reqItem?.serialized;
                  const available = availableSerials[it.product_id] || [];
                  return (
                    <div key={i} className="border border-zinc-100 rounded-md p-3 space-y-2">
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-12 md:col-span-7 text-sm font-semibold">
                          {reqItem?.product_name}
                          {isSer && <span className="ml-2 text-[10px] uppercase font-bold tracking-widest text-[#F97316]">Serialized</span>}
                        </div>
                        <input type="number" value={it.quantity} onChange={e=>{const a=[...issueItems]; a[i]={...a[i], quantity:e.target.value}; setIssueItems(a);}} className="col-span-3 md:col-span-2 input-sm" data-testid={`issue-qty-${i}`} />
                      </div>
                      {isSer && (
                        <div>
                          <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-700 mb-1">Pick Serial Numbers ({available.length} available)</div>
                          {available.length > 0 ? (
                            <select multiple size={Math.min(6, available.length)} value={Array.isArray(it.serial_numbers) ? it.serial_numbers : []}
                              onChange={e=>{const sel = Array.from(e.target.selectedOptions).map(o=>o.value); const a=[...issueItems]; a[i]={...a[i], serial_numbers: sel}; setIssueItems(a);}}
                              className="w-full border border-zinc-200 rounded-md px-2 py-1 text-sm font-mono"
                              data-testid={`issue-serials-${i}`}>
                              {available.map(sn => <option key={sn} value={sn}>{sn}</option>)}
                            </select>
                          ) : (
                            <div className="text-xs text-rose-600">No serials available at this warehouse for this product.</div>
                          )}
                          <div className="text-[11px] text-zinc-500 mt-1">{Array.isArray(it.serial_numbers) ? it.serial_numbers.length : 0} selected. Hold Ctrl/Cmd to pick multiple.</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {issueItems.some((it,i) => r.items[i]?.serialized && (!Array.isArray(it.serial_numbers) || it.serial_numbers.length === 0)) && user.role === "admin" && (
                <label className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="skip-serial-label">
                  <input type="checkbox" checked={skipSerial} onChange={e=>setSkipSerial(e.target.checked)} data-testid="skip-serial-checkbox" className="mt-0.5" />
                  <span>
                    <span className="font-bold text-amber-900">⚠ Not recommended:</span> issue serialized item without capturing serial number.
                    Admin override only — the request will be logged in audit log.
                  </span>
                </label>
              )}
              <button onClick={issue} disabled={!issueWh} data-testid="confirm-issue-btn" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md disabled:opacity-60">Confirm Issue</button>
            </Card>
          )}

          {canUpdateUsage && (
            <Card className="p-5 space-y-3" data-testid="usage-card">
              <div className="font-display font-semibold">Update Material Usage</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Project/Circuit ID" hint="Case-insensitive. Auto-created if new."><input className="input-sm" value={usage.project_id} onChange={e=>setUsage(u=>({...u, project_id: e.target.value}))} data-testid="usage-project" /></Field>
                <Field label="POP ID (Optional)" hint="If material is used at POP."><input className="input-sm" value={usage.pop_id} onChange={e=>setUsage(u=>({...u, pop_id: e.target.value}))} data-testid="usage-pop" placeholder="e.g. POP-MUM-001" /></Field>
                <Field label="Customer"><input className="input-sm" value={usage.customer_name} onChange={e=>setUsage(u=>({...u, customer_name: e.target.value}))} /></Field>
                <Field label="Site Address"><input className="input-sm" value={usage.site_address} onChange={e=>setUsage(u=>({...u, site_address: e.target.value}))} /></Field>
                <Field label="Usage Location">
                  <select className="input-sm" value={usage.usage_location} onChange={e=>setUsage(u=>({...u, usage_location: e.target.value}))} data-testid="usage-location">
                    {["BTS Side","CE Side","Customer Premise","POP","Warehouse","Internal Use"].map(x=><option key={x}>{x}</option>)}
                  </select>
                </Field>
                <Field label="Installation Date"><input type="date" className="input-sm" value={usage.installation_date} onChange={e=>setUsage(u=>({...u, installation_date: e.target.value}))} /></Field>
                <Field label="Remark" full><textarea className="input-sm" rows={2} value={usage.remark} onChange={e=>setUsage(u=>({...u, remark: e.target.value}))} /></Field>
              </div>
              <div className="space-y-2">
                {usage.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center border border-zinc-100 rounded-md p-2">
                    <div className="col-span-12 md:col-span-5 text-sm font-semibold">{r.items[i]?.product_name}</div>
                    <input type="number" value={it.quantity} onChange={e=>{const a=[...usage.items]; a[i]={...a[i], quantity:e.target.value}; setUsage(u=>({...u, items:a}));}} className="col-span-3 md:col-span-2 input-sm" />
                    <input placeholder="Serial #s used" value={Array.isArray(it.serial_numbers)?it.serial_numbers.join(","):it.serial_numbers} onChange={e=>{const a=[...usage.items]; a[i]={...a[i], serial_numbers:e.target.value}; setUsage(u=>({...u, items:a}));}} className="col-span-9 md:col-span-5 input-sm" />
                  </div>
                ))}
              </div>
              <button onClick={submitUsage} data-testid="confirm-usage-btn" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md">Submit Usage Update{doRecovery && " + Recovery"}</button>
              <div className="mt-3 pt-3 border-t border-zinc-100">
                <div className="text-[11px] uppercase tracking-widest font-bold text-zinc-700 mb-2">Site Photos / Attachments</div>
                <FileUpload value={usageAttachments} onChange={setUsageAttachments} testId="usage-attach" />
              </div>

              <div className="mt-3 pt-3 border-t border-zinc-100">
                <label className="inline-flex items-center gap-2 text-sm font-semibold" data-testid="inline-recovery-toggle-label">
                  <input type="checkbox" checked={doRecovery} onChange={e=>setDoRecovery(e.target.checked)} data-testid="inline-recovery-toggle" />
                  Also submit a recovery from this site
                </label>
                {doRecovery && (
                  <div className="mt-3 space-y-3 bg-zinc-50 rounded-md p-3 border border-zinc-100" data-testid="inline-recovery-section">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <select value={recoveryForm.recovered_from} onChange={e=>setRecoveryForm(f=>({...f, recovered_from:e.target.value}))} className="input-sm">
                        {["BTS Side","CE Side","Customer Premise","POP","Other"].map(x=><option key={x}>{x}</option>)}
                      </select>
                      <select value={recoveryForm.returned_to_warehouse_id} onChange={e=>setRecoveryForm(f=>({...f, returned_to_warehouse_id:e.target.value}))} className="input-sm">
                        <option value="">Return To (optional)</option>
                        {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                      </select>
                      <input placeholder="Remark" value={recoveryForm.remark} onChange={e=>setRecoveryForm(f=>({...f, remark:e.target.value}))} className="input-sm" />
                    </div>
                    {recoveryForm.items.map((it, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2">
                        <select value={it.product_id} onChange={e=>{const a=[...recoveryForm.items]; a[i]={...a[i], product_id:e.target.value}; setRecoveryForm(f=>({...f, items:a}));}} className="input-sm col-span-12 md:col-span-5" data-testid={`inline-rec-product-${i}`}>
                          <option value="">Recovered Product</option>
                          {r.items.map((ri, idx) => <option key={idx} value={ri.product_id}>{ri.sku} — {ri.product_name}</option>)}
                        </select>
                        <input type="number" value={it.quantity} onChange={e=>{const a=[...recoveryForm.items]; a[i]={...a[i], quantity:e.target.value}; setRecoveryForm(f=>({...f, items:a}));}} className="input-sm col-span-3 md:col-span-1" />
                        <select value={it.condition} onChange={e=>{const a=[...recoveryForm.items]; a[i]={...a[i], condition:e.target.value}; setRecoveryForm(f=>({...f, items:a}));}} className="input-sm col-span-4 md:col-span-2">
                          {["Working","Faulty","Damaged","Repairable","Scrap","Unknown"].map(x=><option key={x}>{x}</option>)}
                        </select>
                        <input placeholder="Serial #s" value={it.serial_numbers} onChange={e=>{const a=[...recoveryForm.items]; a[i]={...a[i], serial_numbers:e.target.value}; setRecoveryForm(f=>({...f, items:a}));}} className="input-sm col-span-5 md:col-span-4" />
                      </div>
                    ))}
                    <button type="button" onClick={()=>setRecoveryForm(f=>({...f, items:[...f.items,{product_id:"",quantity:1,condition:"Working",serial_numbers:""}]}))} className="text-xs font-semibold text-[#F97316]">+ Add recovery item</button>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-5">
          <Card className="p-5">
            <div className="font-display font-semibold mb-3">Details</div>
            <Detail k="Requester" v={`${r.requester_name} (${r.requester_role})`} />
            <Detail k="Priority" v={r.priority} />
            <Detail k="Customer" v={r.customer_name || "—"} />
            <Detail k="Address" v={r.customer_address || "—"} />
            <Detail k="Required Date" v={r.required_date || "—"} />
            <Detail k="Remark" v={r.remark || "—"} />
          </Card>

          <Card className="p-5">
            <div className="font-display font-semibold mb-3">Approval History</div>
            {(!r.approval_history || r.approval_history.length === 0) ?
              <div className="text-xs text-zinc-500">No approvals yet</div> :
              <div className="space-y-3">
                {r.approval_history.map((a,i)=>(
                  <div key={i} className="text-xs border-l-2 border-[#F97316] pl-3">
                    <div className="font-semibold">{a.by_name} <span className="text-zinc-500">({a.by_role})</span></div>
                    <div className="text-zinc-500">{a.action} · {(a.at||"").slice(0,16).replace("T"," ")}</div>
                    {a.remark && <div className="text-zinc-700 mt-1">{a.remark}</div>}
                  </div>
                ))}
              </div>
            }
          </Card>
        </div>
      </div>
      <style>{`.input-sm { width:100%; border:1px solid #E4E4E7; background:#fff; border-radius:.375rem; padding:.4rem .65rem; font-size:.82rem; outline:none } .input-sm:focus { box-shadow: 0 0 0 2px #F97316; border-color: transparent }`}</style>
    </div>
  );
}

function Detail({ k, v }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-zinc-100 last:border-0 text-sm">
      <div className="text-zinc-500 text-xs uppercase tracking-wider font-semibold">{k}</div>
      <div className="text-right font-medium">{v}</div>
    </div>
  );
}
